import type { ReactNode } from "react";

import type { LalKitabKundliResponse } from "@/lib/api/lal-kitab";

import { DebtCard, PlanetEffectsTable, RemedyCard } from "./LalKitabKundliResults.cards";
import {
  HINDI_OVERALL_LABEL,
  OVERALL_CLASS,
  RESULT_COPY,
  type ResultLocale,
} from "./LalKitabKundliResults.copy";

export default function LalKitabKundliResults({
  result,
  language,
  resultOffer,
}: {
  result: LalKitabKundliResponse;
  language: ResultLocale;
  resultOffer: ReactNode;
}) {
  const overall = result.summary.overall;
  const copy = RESULT_COPY[language];
  const overallLabel = language === "hi" ? HINDI_OVERALL_LABEL[overall] : overallLabelEn(overall);
  return (
    <div className="space-y-8" data-design-owner="lal-kitab-kundli-results">
      <section className="rounded-xl border border-border-default bg-surface-raised p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="mb-1 text-caption font-semibold uppercase tracking-wide text-text-muted">
              {copy.assessment}
            </p>
            <p className={`text-title font-bold ${OVERALL_CLASS[overall] ?? "text-text-primary"}`}>
              {overallLabel ?? overall}
            </p>
          </div>
          <div className="flex gap-6 text-center">
            <SummaryMetric
              label={copy.good}
              value={result.summary.good_placements}
              className="text-intent-success"
            />
            <SummaryMetric
              label={copy.needsUpay}
              value={result.summary.bad_placements}
              className="text-intent-error"
            />
            <SummaryMetric
              label={copy.activeUpay}
              value={result.summary.total_remedies}
              className="text-intent-brand"
            />
          </div>
        </div>
      </section>

      {resultOffer}

      <section>
        <ResultTitle>{copy.planetPlacements}</ResultTitle>
        <div className="rounded-xl border border-border-default bg-surface-raised p-4">
          <PlanetEffectsTable effects={result.planet_effects} language={language} />
        </div>
      </section>

      {result.active_remedies.length > 0 ? (
        <section>
          <ResultTitle>{copy.remedies}</ResultTitle>
          <p className="mb-4 text-body-small text-text-secondary">{copy.remediesBody}</p>
          <div className="space-y-3">
            {result.active_remedies.map((remedy) => (
              <RemedyCard key={remedy.planet} remedy={remedy} language={language} />
            ))}
          </div>
        </section>
      ) : null}

      {result.debts.length > 0 ? (
        <section>
          <ResultTitle>{copy.debts}</ResultTitle>
          <p className="mb-4 text-body-small text-text-secondary">{copy.debtsBody}</p>
          <div className="space-y-3">
            {result.debts.map((debt, index) => (
              <DebtCard key={index} debt={debt} language={language} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className: string;
}) {
  return (
    <div>
      <p className={`text-headline font-bold ${className}`}>{value}</p>
      <p className="text-caption text-text-muted">{label}</p>
    </div>
  );
}

function ResultTitle({ children }: { children: string }) {
  return <h2 className="mb-3 font-serif text-subtitle font-bold text-text-primary">{children}</h2>;
}

function overallLabelEn(overall: string) {
  const labels: Record<string, string> = {
    mostly_positive: "Mostly Positive",
    mixed: "Mixed",
    challenging: "Challenging",
  };
  return labels[overall];
}
